import { KnzDiagnostic, RegistryLookup } from '../types';

/** 開始タグ `<--- name --->` */
const OPEN_RE = /^<---\s*([A-Za-z][\w]*)\s*--->$/;
/** 閉じタグ `<--- !name --->` */
const CLOSE_RE = /^<---\s*!([A-Za-z][\w]*)\s*--->$/;

/** 切り出した生ブロック（解析前） */
export type RawBlock = {
  name: string;
  /** ブロック内の本文行（開始・閉じタグを含まない） */
  lines: string[];
  /** 開始タグの行番号（1-indexed） */
  startLine: number;
};

export type TokenizeResult = {
  blocks: RawBlock[];
  diagnostics: KnzDiagnostic[];
};

/**
 * 本文行（frontmatter 以降）を走査し、`<--- name --->` 〜 `<--- !name --->`
 * でブロックを切り出す。
 *
 * - 開始と閉じの name 不一致・閉じ忘れは error。
 * - `textAs` が `raw` のブロックは、内部に開始/閉じタグ状の行があっても
 *   解釈せず、対応する閉じタグまでを本文としてそのまま取り込む。
 *
 * @param lines 本文行（1 行ずつ）
 * @param baseLine `lines[0]` の実ファイル上の行番号（1-indexed）
 * @param registry raw 判定に使う registry 照会
 */
export const tokenize = (
  lines: string[],
  baseLine: number,
  registry: RegistryLookup,
): TokenizeResult => {
  const blocks: RawBlock[] = [];
  const diagnostics: KnzDiagnostic[] = [];

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();
    const openMatch = trimmed.match(OPEN_RE);

    if (!openMatch) {
      // コメント行（// で始まる）は無視。空行もスキップ。
      if (trimmed !== '' && !trimmed.startsWith('//')) {
        const closeMatch = trimmed.match(CLOSE_RE);
        diagnostics.push({
          severity: 'warning',
          message: closeMatch
            ? `対応する開始タグのない閉じタグです: ${trimmed}`
            : `ブロック外のテキストは無視されます: ${trimmed}`,
          line: baseLine + i,
        });
      }
      i++;
      continue;
    }

    const name = openMatch[1];
    const startLine = baseLine + i;
    const isRaw = registry.getTextAs(name) === 'raw';
    const bodyLines: string[] = [];
    let closed = false;
    let j = i + 1;

    for (; j < lines.length; j++) {
      const inner = lines[j];
      const innerTrimmed = inner.trim();
      const closeMatch = innerTrimmed.match(CLOSE_RE);

      if (closeMatch && closeMatch[1] === name) {
        closed = true;
        break;
      }

      // raw でない場合のみ、ネストした開始タグ・別名の閉じタグを異常として扱う。
      if (!isRaw) {
        const innerOpen = innerTrimmed.match(OPEN_RE);
        const innerClose = closeMatch;
        if (innerOpen) {
          diagnostics.push({
            severity: 'error',
            message: `ブロック \`${name}\` が閉じられる前に別のブロック \`${innerOpen[1]}\` が始まっています。入れ子は children で表現してください。`,
            line: baseLine + j,
          });
          break;
        }
        if (innerClose && innerClose[1] !== name) {
          diagnostics.push({
            severity: 'error',
            message: `ブロック \`${name}\` の閉じタグと一致しません: ${innerTrimmed}`,
            line: baseLine + j,
          });
          break;
        }
      }

      bodyLines.push(inner);
    }

    if (!closed) {
      diagnostics.push({
        severity: 'error',
        message: `ブロック \`${name}\` が閉じられていません（\`<--- !${name} --->\` がありません）。`,
        line: startLine,
      });
      // 閉じられていない場合はここまでのブロックを破棄して先へ進む
      i = j + 1;
      continue;
    }

    blocks.push({ name, lines: bodyLines, startLine });
    i = j + 1;
  }

  return { blocks, diagnostics };
};
